import React, {
  useMemo,
  useState,
  useEffect
} from 'react';
import PropTypes from 'prop-types';

import { AnimatePresence } from '@sopranodesign/framer-motion';
import {
  Flex,
  Table,
  Button,
  TableRow,
  Paragraph,
  TableBody,
  TableCell,
  TableHead,
  Pagination,
  PanelSlider,
  TableActions,
  LoadingContainer,
  OrderableTableRow,
  usePaginationReducer,
  useOrderableTableReducer
} from '@sopranodesign/pulse-components';
import { useQuery } from '@sopranodesign/pulse-hooks';
import {
  useAuth,
  PermissionFilter
} from '@sopranodesign/pulse-auth';

import { fetchArticles } from '../api';
import { StateLevel } from '.';

const ZendeskArticlesList = ({
  categoryId,
  handleHide,
  onOpenTagsModal,
  onOpenRecrawlModal,
  loadingIds,
  errorIds
}) => {
  const { hasPermission } = useAuth();
  const canViewTag = hasPermission('view generative ai tag');
  const canEditTag = hasPermission('edit generative ai tag');
  const canEditResource = hasPermission('edit generative ai resource');

  const [shouldRefetch, setShouldRefetch] = useState(false);

  const order = useOrderableTableReducer({ id: `zendesk-${categoryId}` });
  const pagination = usePaginationReducer({ id: `zendesk-${categoryId}` });

  const { data, isLoading } = useQuery({
    queryKey: [
      'generative-ai/zendesk/articles',
      categoryId,
      { ...pagination.params, ...order.params }
    ],
    queryFn: () => fetchArticles(categoryId, { ...pagination.params, ...order.params }),
    keepPreviousData: true,
    refetchInterval: shouldRefetch ? 5000 : false
  });

  const response = useMemo(() => data?.data ?? {}, [data]);
  const articles = useMemo(() => response.data ?? [], [response]);
  const meta = response.meta ?? {};
  const categoryName = response.category_name ?? 'Articles';

  const globalHasPending = response.has_pending ?? false;

  useEffect(() => {
    setShouldRefetch( globalHasPending);
  }, [globalHasPending]);

  return (
    <AnimatePresence>
      <PanelSlider header={categoryName} handleHide={handleHide}>
        {isLoading && <LoadingContainer />}
        {!isLoading && !articles.length && (
          <Paragraph mb secondary>No articles available.</Paragraph>
        )}
        {!isLoading && articles.length > 0 && (
          <Flex col>
            <Flex border xScroll>
              <Table>
                <TableHead>
                  <OrderableTableRow
                    rows={[
                      { label: 'Title', sort: 'title' },
                      { label: 'Status', sort: 'status' },
                      { label: 'Crawled At', sort: 'updated_at' },
                      ...(canViewTag ? [{ label: 'Tags' }] : []),
                      ...(((canViewTag && canEditTag) || canEditResource) ? [null] : [])
                    ]}
                    {...order.props}
                  />
                </TableHead>
                <TableBody>
                  {articles.map(article => (
                    <TableRow key={article.id}>
                      <TableCell>{article.title}</TableCell>
                      <TableCell>
                        <Flex center>
                          <StateLevel level={article.status} />
                          <span>{article.status}</span>
                        </Flex>
                      </TableCell>
                      <TableCell>{article.updated_at}</TableCell>

                      <PermissionFilter can="view generative ai tag">
                        <TableCell>
                          {article.groups && article.groups.length > 0
                            ? article.groups.map(group => group.name).join(', ')
                            : 'No Tags'}
                        </TableCell>
                      </PermissionFilter>

                      {((canViewTag && canEditTag) || canEditResource) && (
                        <TableActions>
                          <PermissionFilter can="view generative ai tag">
                            <PermissionFilter can="edit generative ai tag">
                              {article.status === 'Success' && (
                                <Button
                                  icon="tag"
                                  onClick={() => onOpenTagsModal(article)}
                                >
                                  Tags
                                </Button>
                              )}
                            </PermissionFilter>
                          </PermissionFilter>

                          <PermissionFilter can="edit generative ai resource">
                            <Button
                              icon="download"
                              loading={Boolean(loadingIds?.[article.id])}
                              onClick={() => onOpenRecrawlModal(article)}
                            >
                              Recrawl
                            </Button>
                          </PermissionFilter>

                          {errorIds?.[article.id] && (
                            <p style={{ color: 'red', marginTop: '4px' }}>
                              {errorIds[article.id]}
                            </p>
                          )}
                        </TableActions>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Flex>

            {typeof meta.current_page === 'number' && (
              <Pagination pagination={meta} {...pagination.props} />
            )}
          </Flex>
        )}
      </PanelSlider>
    </AnimatePresence>
  );
};

ZendeskArticlesList.propTypes = {
  categoryId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  handleHide: PropTypes.func.isRequired,
  onOpenTagsModal: PropTypes.func.isRequired,
  onOpenRecrawlModal: PropTypes.func.isRequired,
  loadingIds: PropTypes.object,
  errorIds: PropTypes.object.isRequired
};

export default ZendeskArticlesList;